// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getOrganizationUsers } from '@/account/api/get-organization-users';
import { Person, personSchema } from '@/account/api/types';
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { Protected } from '@/adornments/components/protected';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { usePermissionChecker } from '@/authorization/hooks/use-user-policy';
import { Permission } from '@/authorization/types';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDatePicker } from '@/components/form-date-picker';
import { FormPersonSearch } from '@/components/form-person-search';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { Add, Remove } from '@/constants/hardfin-standard-icons';
import { createTimeRecord } from '@/costs/api/create-time-record';
import { CreateUpdateTimeRecord, TimeRecord } from '@/costs/api/types';
import { updateTimeRecord } from '@/costs/api/update-time-record';
import { FormCostCategoryAutocomplete } from '@/costs/components/form-cost-category-autocomplete';
import { computeTimeRecordTotal } from '@/costs/utilities/time-record-total';
import {
  Currency,
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { ProjectAutocomplete } from '@/project/components/project-autocomplete';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import { SparseProject, sparseProjectSchema } from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

// Time records keep a lower rate ceiling ($999,999.99) than the general
// currency maximum; the empty amount clears the rate.
const MAX_HOURLY_RATE = new Decimal('999999.99');
// Places shown for the unrounded total in the rounding caption.
const UNROUNDED_TOTAL_DIGITS = 4;
const hourlyRateSchema = clearableCurrencyInputSchema.refine(
  (value) => value === null || value.amount.lessThanOrEqualTo(MAX_HOURLY_RATE),
  { message: 'Rate must be between $0.00 and $999,999.99' }
);

const formSchema = z
  .object({
    hours: z.number().min(0),
    minutes: z.number().min(0).max(59),
    hourlyRate: hourlyRateSchema,
    category: z.string().min(1, 'Category is required').nullable(),
    description: z
      .string()
      .min(1, 'Description is required')
      .max(2000, 'Description cannot exceed 2000 characters'),
    incurredOn: dateSchema.nullable(),
    worker: personSchema.nullable(),
    project: sparseProjectSchema.nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.incurredOn === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Date is required',
        path: ['incurredOn'],
      });
    }
    if (data.category === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Category is required',
        path: ['category'],
      });
    }
    if (data.worker === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Person is required',
        path: ['worker'],
      });
    }

    const totalMinutes = data.hours * 60 + data.minutes;
    if (totalMinutes <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Time duration must be greater than 0',
        path: ['hours'],
      });
    }

    if (totalMinutes > 1440) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Total time cannot exceed 24 hours',
        path: ['hours'],
      });
    }
  });

interface FormData {
  hours: number;
  minutes: number;
  hourlyRate: SerializableCurrency;
  category: string | null;
  description: string;
  incurredOn: DateTime | null;
  worker: Person | null;
  project: SparseProject | null;
}

type FormOutput = z.output<typeof formSchema>;

interface Props {
  /**
   * The project to pre-fill in the project selector.
   * If provided, this project will be selected by default but can be changed.
   */
  initialProject?: SparseProject;

  /**
   * The time record to edit. If provided, dialog is in edit mode.
   */
  initialTimeRecord?: Omit<
    TimeRecord,
    'createdBy' | 'createdAt' | 'lastUpdatedBy' | 'lastUpdatedAt'
  >;
  /**
   * If true, the dialog is open.
   */
  isOpen: boolean;

  /**
   * A callback fired when the dialog requests to be closed.
   */
  onClose: () => void;

  /**
   * Additional query keys to invalidate on successful time record creation/update.
   */
  invalidationKeys?: string[][];
}

/**
 * A dialog for creating or editing a time record with detailed time tracking information.
 */
export function TimeRecordDialog(props: Props) {
  const queryClient = useQueryClient();
  const isEditing = !!props.initialTimeRecord;

  // State for permission error banner
  const [permissionError, setPermissionError] = useState<string | null>(null);

  // Get current user for default worker selection
  const { user: currentUser } = useCurrentUser();

  // Check permissions for rate field
  const { hasPermission } = usePermissionChecker();
  const rateReadPermission = hasPermission('time.rate', 'read');
  const rateEditPermission = hasPermission(
    'time.rate',
    isEditing ? 'update' : 'create'
  );

  const canReadRate =
    rateReadPermission === Permission.PermissionExplicitTrue ||
    rateReadPermission === Permission.PermissionImplicitTrue;
  const canEditRate =
    rateEditPermission === Permission.PermissionExplicitTrue ||
    rateEditPermission === Permission.PermissionImplicitTrue;

  // Memoize today to prevent constant recalculation
  const today = useMemo(() => DateTime.now().startOf('day'), []);

  // Get projects for the autocomplete
  const { projects: allProjects, isLoading: isLoadingProjects } =
    useSparseProjectList();

  // Get organization users to validate if current user is a member
  const usersQuery = useQuery({
    queryKey: ['organization-users', currentUser?.organization.key],
    queryFn: () => getOrganizationUsers(currentUser!.organization.key),
    enabled: !!currentUser,
  });

  // Convert current user to person format for the worker field, but only if they exist in organization users
  const currentUserAsPerson = useMemo((): Person | null => {
    if (!currentUser || !usersQuery.data) return null;

    // Check if current user exists in the organization users list
    const userExistsInOrg = usersQuery.data.users.some(
      (orgUser) => orgUser.id === currentUser.id && !orgUser.isDeactivated
    );

    // Only return the current user as person if they exist in the organization
    if (userExistsInOrg) {
      return {
        id: currentUser.id,
        key: currentUser.key,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
      };
    }

    return null;
  }, [currentUser, usersQuery.data]);

  // Get current user's default rate for initial population
  const currentUserDefaultRate = useMemo((): Currency | null => {
    if (!currentUser || !usersQuery.data) return null;

    // Find the current user in organization users list to get their default rate
    const orgUser = usersQuery.data.users.find(
      (user) => user.id === currentUser.id && !user.isDeactivated
    );

    return orgUser?.defaultRate ?? null;
  }, [currentUser, usersQuery.data]);

  // Calculate default values that only change when props change
  const defaultValues = useMemo((): FormData => {
    // Convert total minutes back to hours and minutes for editing
    const totalMinutes = props.initialTimeRecord?.totalMinutes || 0;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const rateValue: Currency | null = isEditing
      ? (props.initialTimeRecord?.rate ?? null)
      : canReadRate && currentUserAsPerson
        ? currentUserDefaultRate
        : null;

    const baseValues = {
      hours: hours,
      minutes: minutes,
      hourlyRate: toCurrencyInputValue(
        rateValue,
        currentUser.organization.defaultCurrency
      ),
      category: props.initialTimeRecord?.category || '',
      description: props.initialTimeRecord?.description || '',
      incurredOn: props.initialTimeRecord?.incurredOn || today,
    };

    if (isEditing) {
      return {
        ...baseValues,
        worker: props.initialTimeRecord?.worker ?? null,
        project: props.initialTimeRecord?.attachedProject ?? null,
      };
    }

    return {
      ...baseValues,
      worker: currentUserAsPerson || null,
      project: props.initialProject || null,
    };
  }, [
    isEditing,
    props.initialTimeRecord?.totalMinutes,
    props.initialTimeRecord?.rate,
    props.initialTimeRecord?.category,
    props.initialTimeRecord?.description,
    props.initialTimeRecord?.incurredOn,
    props.initialTimeRecord?.worker,
    props.initialTimeRecord?.attachedProject,
    props.initialProject,
    canReadRate,
    currentUserAsPerson,
    currentUserDefaultRate,
    currentUser.organization.defaultCurrency,
    today,
  ]);

  const timeRecordMutation = useMutation({
    mutationFn: (data: FormOutput) => {
      const totalMinutes = data.hours * 60 + data.minutes;

      // Handle rate based on permissions:
      // - If user can't read rates, set to undefined (don't send to server)
      // - If user can read but rate is empty/null, set to null
      // - Otherwise send the serialized currency value
      let hourlyRate: CreateUpdateTimeRecord['hourlyRate'];
      if (!canReadRate) {
        hourlyRate = undefined;
      } else {
        hourlyRate = data.hourlyRate ?? null;
      }

      const payload: CreateUpdateTimeRecord = {
        totalMinutes: totalMinutes,
        hourlyRate,
        category: data.category!,
        description: data.description || null,
        incurredOn: data.incurredOn!, // Validation prevents this from ever being null
        workerId: data.worker!.id, // Validation prevents this from ever being null
        projectId: data.project?.id || null,
      };

      if (isEditing && props.initialTimeRecord) {
        return updateTimeRecord(props.initialTimeRecord.key, payload);
      }
      return createTimeRecord(payload);
    },
    onSuccess: (resp) => {
      // Invalidate default time record queries
      queryClient.invalidateQueries({
        queryKey: ['time-records'],
      });

      // Invalidate any additional keys provided
      if (props.invalidationKeys) {
        props.invalidationKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      // Reset form with the server response values
      const totalMinutes = resp.timeRecord.totalMinutes || 0;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      reset({
        hours: hours,
        minutes: minutes,
        hourlyRate: toCurrencyInputValue(
          resp.timeRecord.rate,
          currentUser.organization.defaultCurrency
        ),
        category: resp.timeRecord.category,
        description: resp.timeRecord.description || '',
        incurredOn: resp.timeRecord.incurredOn,
        worker: resp.timeRecord.worker || null,
        project: resp.timeRecord.attachedProject || null,
      });
      props.onClose();
    },
    onError: (error: AxiosError) => {
      // Handle 403 permission errors with custom message
      if (error.response?.status === 403) {
        setPermissionError(
          'Sorry, you don’t have permission to update the “rate” field. Please retry. For access, contact your administrator.'
        );
        return;
      }
    },
  });

  const { control, reset, handleSubmit, formState, setValue, watch, trigger } =
    useForm<FormData, unknown, FormOutput>({
      resolver: zodResolver(formSchema),
      defaultValues,
    });

  // Watch form values for use in callbacks and calculations
  const hours = useWatch({
    name: 'hours',
    control,
  });

  const minutes = useWatch({
    name: 'minutes',
    control,
  });

  const hourlyRate = useWatch({
    name: 'hourlyRate',
    control,
  });

  // Track the last props we used to reset the form
  const lastResetPropsRef = useRef<string>('');

  // Create a stable string representation of the relevant props
  const propsKey = useMemo(() => {
    return JSON.stringify({
      isOpen: props.isOpen,
      initialTimeRecordKey: props.initialTimeRecord?.key,
      initialTotalMinutes: props.initialTimeRecord?.totalMinutes,
      initialRate: props.initialTimeRecord?.rate
        ? `${props.initialTimeRecord.rate.amount.toString()} ${props.initialTimeRecord.rate.isoCode}`
        : null,
      initialCategory: props.initialTimeRecord?.category,
      initialDescription: props.initialTimeRecord?.description,
      initialDate: props.initialTimeRecord?.incurredOn?.toISODate(),
      initialProjectId:
        props.initialTimeRecord?.attachedProject?.id ||
        props.initialProject?.id,
      initialWorkerId: props.initialTimeRecord?.worker?.id,
      defaultWorkerId: isEditing ? undefined : currentUserAsPerson?.id,
      defaultRate:
        !isEditing && canReadRate && currentUserDefaultRate
          ? `${currentUserDefaultRate.amount.toString()} ${currentUserDefaultRate.isoCode}`
          : undefined,
    });
  }, [
    isEditing,
    canReadRate,
    props.isOpen,
    props.initialTimeRecord?.key,
    props.initialTimeRecord?.totalMinutes,
    props.initialTimeRecord?.rate,
    props.initialTimeRecord?.category,
    props.initialTimeRecord?.description,
    props.initialTimeRecord?.incurredOn,
    props.initialTimeRecord?.attachedProject?.id,
    props.initialTimeRecord?.worker?.id,
    props.initialProject?.id,
    currentUserAsPerson?.id,
    currentUserDefaultRate,
  ]);

  useEffect(() => {
    // Only reset if:
    // 1. Dialog is open
    // 2. Props have actually changed
    // 3. User hasn't made any changes yet (form is not dirty)
    if (
      props.isOpen &&
      propsKey !== lastResetPropsRef.current &&
      !formState.isDirty
    ) {
      reset(defaultValues);
      lastResetPropsRef.current = propsKey;
    }
  }, [props.isOpen, propsKey, defaultValues, reset, formState.isDirty]);

  // Clear the last reset props and permission error when dialog closes
  useEffect(() => {
    if (!props.isOpen) {
      lastResetPropsRef.current = '';
      setPermissionError(null);
    }
  }, [props.isOpen]);

  // Handle initial population of current user's default rate for new records
  useEffect(() => {
    // Only run if user has permission to see rates
    if (
      props.isOpen &&
      !isEditing &&
      canReadRate &&
      currentUserAsPerson &&
      !formState.isDirty
    ) {
      const currentWorker = watch('worker');
      // Only set default rate if current user is the selected worker and has a default rate
      if (
        currentWorker?.id === currentUserAsPerson.id &&
        currentUserDefaultRate
      ) {
        setValue(
          'hourlyRate',
          toCurrencyInputValue(
            currentUserDefaultRate,
            currentUser.organization.defaultCurrency
          )
        );
      }
    }
  }, [
    props.isOpen,
    isEditing,
    canReadRate,
    currentUserAsPerson,
    currentUserDefaultRate,
    currentUser.organization.defaultCurrency,
    formState.isDirty,
    watch,
    setValue,
  ]);

  const close = () => {
    props.onClose();
    reset(defaultValues);
    setPermissionError(null);
  };

  const handleValidSubmission: SubmitHandler<FormOutput> = (data) => {
    timeRecordMutation.mutate(data);
  };

  // Helper functions for time controls
  const adjustHours = (adjustment: number) => {
    const currentHours = hours || 0;
    const newHours = Math.max(0, currentHours + adjustment);
    setValue('hours', newHours);
    // Only revalidate if there's already an error
    if (formState.errors.hours) {
      trigger('hours');
    }
  };

  const adjustMinutes = (adjustment: number) => {
    const currentMinutes = minutes || 0;
    const newMinutes = Math.max(0, Math.min(59, currentMinutes + adjustment));
    setValue('minutes', newMinutes);
    // Only revalidate if there's already an error (hours field contains the refinement)
    if (formState.errors.hours) {
      trigger('hours');
    }
  };

  const setQuickHours = (quickHours: number) => {
    setValue('hours', quickHours);
    // Only revalidate if there's already an error
    if (formState.errors.hours) {
      trigger('hours');
    }
  };

  const setQuickMinutes = (quickMinutes: number) => {
    setValue('minutes', quickMinutes);
    // Only revalidate if there's already an error (hours field contains the refinement)
    if (formState.errors.hours) {
      trigger('hours');
    }
  };

  // Calculate total amount for display
  const totalMinutes = (hours || 0) * 60 + (minutes || 0);
  const rate = parseCurrencyAmount(hourlyRate?.amount ?? '');

  const timeRecordTotal =
    rate !== null && totalMinutes > 0
      ? computeTimeRecordTotal(
          { amount: rate, isoCode: hourlyRate.isoCode },
          totalMinutes
        )
      : null;
  const totalAmount = timeRecordTotal?.total ?? null;
  const roundingCaption =
    timeRecordTotal !== null && rate !== null && timeRecordTotal.isRounded
      ? `Rounded from ${formatCurrencyValue({
          amount: timeRecordTotal.unroundedAmount.toDecimalPlaces(
            UNROUNDED_TOTAL_DIGITS,
            Decimal.ROUND_HALF_UP
          ),
          isoCode: hourlyRate.isoCode,
        })} (${totalMinutes} min × ${formatCurrencyValue({
          amount: rate,
          isoCode: hourlyRate.isoCode,
        })}/h)`
      : undefined;

  // Helper function to format duration as "Xh Ym"
  const formatDuration = (totalMinutes: number): string => {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h > 0 && m > 0) {
      return `${h}h ${m}m`;
    } else if (h > 0) {
      return `${h}h`;
    } else if (m > 0) {
      return `${m}m`;
    }
    return '0m';
  };

  // Check for different time thresholds
  const exceedsEightHours = totalMinutes > 480; // 8 hours
  const exceedsSixteenHours = totalMinutes > 960; // 16 hours
  const exceedsTwentyFourHours = totalMinutes > 1440; // 24 hours
  const durationText = formatDuration(totalMinutes);

  // Determine alert severity and message
  const getTimeAlert = () => {
    if (exceedsTwentyFourHours) {
      return {
        severity: 'error' as const,
        message: `Invalid time entry: ${durationText} exceeds the 24-hour maximum`,
      };
    } else if (exceedsSixteenHours) {
      return {
        severity: 'warning' as const,
        message: `Very long day: ${durationText} exceeds 16 hours`,
      };
    } else if (exceedsEightHours) {
      return {
        severity: 'info' as const,
        message: `Long day: ${durationText} exceeds 8 hours`,
      };
    }
    return null;
  };

  const timeAlert = getTimeAlert();

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={
          timeRecordMutation.isError &&
          !permissionError &&
          !isPlanLimitError(timeRecordMutation.error)
        }
        message={`An error occurred while ${isEditing ? 'updating' : 'adding'} the time record.`}
        onClose={timeRecordMutation.reset}
      />
      <DialogTitle>{isEditing ? 'Edit time' : 'Add time'}</DialogTitle>
      <DialogContent dividers>
        {permissionError && (
          <Alert
            severity="error"
            onClose={() => setPermissionError(null)}
            sx={{
              mb: 3,
              borderRadius: 1,
            }}
          >
            {permissionError}
          </Alert>
        )}
        <Grid container spacing={3}>
          {/* Person - Left */}
          <Grid item xs={6}>
            <FormPersonSearch
              label="Person"
              control={control}
              name="worker"
              onChange={(person) => {
                // Only handle rate changes if user has permission to see rates
                if (canReadRate) {
                  // Always set to the person's default rate (cleared if no person or no default)
                  setValue(
                    'hourlyRate',
                    toCurrencyInputValue(
                      person?.defaultRate ?? null,
                      currentUser.organization.defaultCurrency
                    )
                  );
                }
              }}
            />
          </Grid>

          {/* Date - Right */}
          <Grid item xs={6}>
            <FormDatePicker
              disableFuture
              control={control}
              name="incurredOn"
              label="Date"
            />
          </Grid>

          {/* Hours - Left Cell */}
          <Grid item xs={6} sx={{ mb: 0.5 }}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <IconButton
                  size="small"
                  onClick={() => adjustHours(-1)}
                  disabled={hours <= 0}
                  tabIndex={-1}
                  sx={{
                    '&.Mui-disabled': {
                      color: 'text.disabled',
                      opacity: 0.4,
                    },
                  }}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <TextField
                  size="small"
                  value={hours || 0}
                  onChange={(e) => {
                    setValue('hours', parseInt(e.target.value) || 0);
                    // Only revalidate if there's already an error
                    if (formState.errors.hours) {
                      trigger('hours');
                    }
                  }}
                  onBlur={() => {
                    // Only revalidate if there's already an error
                    if (formState.errors.hours) {
                      trigger('hours');
                    }
                  }}
                  inputProps={{
                    style: { textAlign: 'center' },
                    min: 0,
                  }}
                  type="number"
                  sx={{ width: '80px' }}
                  error={!!formState.errors.hours}
                />
                <IconButton
                  size="small"
                  onClick={() => adjustHours(1)}
                  tabIndex={-1}
                  sx={{
                    '&.Mui-disabled': {
                      color: 'text.disabled',
                      opacity: 0.4,
                    },
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
                <Typography variant="body2">hours</Typography>
              </Box>
              <Box display="flex" gap={0.5}>
                {[2, 3, 4, 8].map((quickHour) => (
                  <Chip
                    key={quickHour}
                    label={`${quickHour}h`}
                    size="small"
                    variant={hours === quickHour ? 'filled' : 'outlined'}
                    onClick={() => setQuickHours(quickHour)}
                    clickable
                    sx={{ px: 1 }}
                    tabIndex={-1}
                  />
                ))}
              </Box>
            </Box>
            {formState.errors.hours && (
              <Typography
                variant="caption"
                color="error"
                sx={{ ml: 2, fontSize: '0.75rem', display: 'block' }}
              >
                {formState.errors.hours.message}
              </Typography>
            )}
          </Grid>

          {/* Minutes - Right Cell */}
          <Grid item xs={6} sx={{ mb: 0.5 }}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <IconButton
                  size="small"
                  onClick={() => adjustMinutes(-5)}
                  disabled={minutes <= 0}
                  tabIndex={-1}
                  sx={{
                    '&.Mui-disabled': {
                      color: 'text.disabled',
                      opacity: 0.4,
                    },
                  }}
                >
                  <Remove fontSize="small" />
                </IconButton>
                <TextField
                  size="small"
                  value={minutes || 0}
                  onChange={(e) => {
                    const value = Math.max(
                      0,
                      Math.min(59, parseInt(e.target.value) || 0)
                    );
                    setValue('minutes', value);
                    // Only revalidate if there's already an error (hours field contains the refinement)
                    if (formState.errors.hours) {
                      trigger('hours');
                    }
                  }}
                  onBlur={() => {
                    // Only revalidate if there's already an error (hours field contains the refinement)
                    if (formState.errors.hours) {
                      trigger('hours');
                    }
                  }}
                  inputProps={{
                    style: { textAlign: 'center' },
                    min: 0,
                    max: 59,
                  }}
                  type="number"
                  sx={{ width: '80px' }}
                  error={!!formState.errors.hours}
                />
                <IconButton
                  size="small"
                  onClick={() => adjustMinutes(5)}
                  disabled={minutes >= 55}
                  tabIndex={-1}
                >
                  <Add fontSize="small" />
                </IconButton>
                <Typography variant="body2">minutes</Typography>
              </Box>
              <Box display="flex" gap={0.5}>
                {[15, 30, 45].map((quickMinute) => (
                  <Chip
                    key={quickMinute}
                    label={`${quickMinute}m`}
                    size="small"
                    variant={minutes === quickMinute ? 'filled' : 'outlined'}
                    onClick={() => setQuickMinutes(quickMinute)}
                    clickable
                    sx={{ px: 1 }}
                    tabIndex={-1}
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Time Alert - Full Width */}
          {timeAlert && (
            <Grid
              item
              xs={12}
              sx={{ paddingTop: '0 !important', paddingBottom: '0 !important' }}
            >
              <Alert severity={timeAlert.severity} sx={{ mt: 1 }}>
                {timeAlert.message}
              </Alert>
            </Grid>
          )}

          {/* Rate - Left (conditionally rendered based on read permission) */}
          {canReadRate && (
            <Grid item xs={6}>
              <FormCurrencyTextField
                name="hourlyRate"
                label="Rate (per hour)"
                placeholder="0.00"
                control={control}
                fullWidth
                disabled={!canEditRate}
                protected
              />
            </Grid>
          )}

          {/* Total - Right (only show if rate field is visible) */}
          {canReadRate && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Total"
                value={
                  totalAmount !== null ? formatCurrencyValue(totalAmount) : ''
                }
                helperText={roundingCaption}
                InputProps={{
                  readOnly: true,
                  disabled: true,
                  style: { color: totalAmount !== null ? 'inherit' : '#999' },
                  endAdornment: <Protected />,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    textAlign: 'right',
                    color: totalAmount !== null ? 'inherit' : '#999',
                  },
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Project - Left */}
          <Grid item xs={6}>
            <Controller
              name="project"
              control={control}
              render={({ field: { onChange, value } }) => (
                <ProjectAutocomplete
                  size="small"
                  label="Project"
                  placeholder="No project"
                  options={allProjects}
                  value={value}
                  onChange={(_event, newValue) => {
                    onChange(newValue);
                  }}
                  loading={isLoadingProjects}
                  filterOptions={(options, state) => {
                    // Hide archived projects when no search input
                    if (state.inputValue === '') {
                      return options.filter((option) => !option.isArchived);
                    }

                    const lowerSearchTerm = state.inputValue.toLowerCase();
                    const showArchived = lowerSearchTerm === 'archived';

                    // Filter options based on search input
                    return options.filter((option) => {
                      const labelMatches = option.name
                        .toLowerCase()
                        .includes(lowerSearchTerm);
                      const isArchivedMatch = showArchived && option.isArchived;

                      return labelMatches || isArchivedMatch;
                    });
                  }}
                />
              )}
            />
          </Grid>

          {/* Category - Right */}
          <Grid item xs={6}>
            <FormCostCategoryAutocomplete
              name="category"
              control={control}
              label="Category"
              placeholder="Type or select category"
              type="time"
            />
          </Grid>

          {/* Description - Full Width Below */}
          <Grid item xs={12}>
            <Stack spacing={1}>
              <FormTextField
                name="description"
                label="Description"
                control={control}
                isMultiline
                minimumRows={3}
                fullWidth
              />

              {/* Character Counter */}
              <Box display="flex" justifyContent="flex-end">
                <Typography
                  variant="caption"
                  color={
                    (watch('description')?.length || 0) > 2000
                      ? 'error'
                      : 'text.secondary'
                  }
                >
                  {`${watch('description')?.length || 0}/2000`}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
          disabled={timeRecordMutation.isPending}
        >
          {isEditing ? 'Update time' : 'Add time'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
